/**
 * localStorage that cannot take the page down with it.
 *
 * Safari set to block site storage, and private browsing on some browsers,
 * don't just return null - touching the API throws. The bare identifier throws
 * a ReferenceError ("Can't find variable: localStorage"), and `window.
 * localStorage` throws a SecurityError. Because the reads here sit inside
 * mount effects, an unguarded one took the whole render down: a real user on
 * app.doqit.io got a white screen off the back of a preference we only wanted
 * for RevenueCat bookkeeping (DOQIT-APP-1J).
 *
 * None of what we keep in here is load-bearing - it is ids and UI preferences
 * that regenerate or fall back to a default. So the right behaviour when
 * storage is unavailable is to carry on without it: reads return null, writes
 * do nothing.
 */
export const safeStorage = {
  getItem(key: string): string | null {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem(key: string, value: string): void {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      // Storage unavailable - see the note above on why this is safe to skip.
    }
  },
  removeItem(key: string): void {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // Storage unavailable - see the note above on why this is safe to skip.
    }
  },
};
